import ErrorIcon from "@mui/icons-material/Error";
import { Box, CircularProgress, Link, Typography } from "@mui/material";
import clsx from "clsx";
import { GithubAuthProvider, GoogleAuthProvider, OAuthProvider } from "firebase/auth";
import { Form, Formik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import YupPassword from "yup-password";
import Github from "../../../assets/images/icons/Github.tsx";
import Google from "../../../assets/images/icons/Google.tsx";
import Microsoft from "../../../assets/images/icons/Microsoft.tsx";
import Agreement from "../../../components/common/Agreement/Agreement.tsx";
import BriaButton from "../../../components/common/BriaButton/BriaButton.tsx";
import BriaInput from "../../../components/common/BriaInput/BriaInput.tsx";
import StaticDropdown from "../../../components/common/DropDowns/StaticDropdown.tsx";
import { APPS } from "../../../constants/AppsConstants.ts";
import RouterConstants from "../../../constants/RouterConstants.ts";
import { MICROSOFT_PROVIDER_ID } from "../../../constants/common.ts";
import { useAuthService } from "../../../hooks/useAuthService.tsx";
import useSecureNavigate from "../../../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../../../hooks/useStores.tsx";
import { capitalizeFirstLetter } from "../../../utils";
import { DROPDOWN_MAX_HEIGHT, DROPDOWN_WIDTH } from "../../Account/UsageAndBilling/UsageAndBillingUtils.tsx";
import MfaVerification from "../Login/MfaVerification/MfaVerification.tsx";
import classes from "./Register.module.scss";

YupPassword(Yup);

const Register = () => {
	const initialValues = {
		username: "",
		email: "",
		password: "",
		company: "",
		role: "",
		feedback: "",
	};

	const navigate = useSecureNavigate();
	const { authStore } = useAppStore();
	const { t } = useTranslation();
	const [email, setEmail] = useState<string>("");
	const [userRole, setUserRole] = useState<string | undefined>();
	const [mfaResolver, setMfaResolver] = useState<any>(null);
	const [mfaEmail, setMfaEmail] = useState<string>("");
	const { errorMessage, authenticateWithPopup, registerWithEmail, loading } = useAuthService();
	const queryParams = new URLSearchParams(location.search);
	const continueUrl = queryParams.get("continueUrl") || undefined;
	const loc = useLocation();
	const state = loc.state;
	const continueFieldsHeight = "50px";

	const handleContinue = (values: any) => {
		setEmail(values.email);
	};

	const signUpUsingProvider = async (authProvider: any) => {
		if (continueUrl) {
			if (continueUrl.includes("apps")) {
				authStore.registrationSource = "apps";
			} else if (continueUrl?.includes(APPS.AUTOMOTIVE)) {
				authStore.registrationSource = APPS.AUTOMOTIVE;
			}
		} else {
			authStore.registrationSource = "console";
		}
		await authenticateWithPopup(authProvider, undefined, (resolver, email) => {
			setMfaEmail(email);
			setMfaResolver(resolver);
		});
		authStore.registrationSource = undefined;
		if (continueUrl) {
			navigate(continueUrl, { state: state });
		}
	};

	const handleSubmit = (values: any) => {
		if (continueUrl) {
			if (continueUrl.includes("apps")) {
				authStore.registrationSource = "apps";
			} else if (continueUrl?.includes(APPS.AUTOMOTIVE)) {
				authStore.registrationSource = APPS.AUTOMOTIVE;
			}
		} else {
			authStore.registrationSource = "console";
		}
		registerWithEmail(
			values.username,
			values.email ? values.email : email,
			values.password,
			values.company,
			userRole,
		)
			.then(() => {
				authStore.registrationSource = undefined;
				if (continueUrl) {
					navigate(continueUrl, { state: state });
				}
			})
			.catch((error) => {
				console.log(error);
			});
	};

	return (
		<Box className={classes.content}>
			<Typography className={classes.mainTitle}>
				{mfaResolver ? t("mfaLoginVerification.title") : !email ? t("getStartedWithBria") : t("createAccount")}
			</Typography>

			{mfaResolver ? (
				<MfaVerification
					mfaResolver={mfaResolver}
					continueUrl={continueUrl}
					state={state}
					navigate={navigate}
					onBackToLogin={() => setMfaResolver(null)}
					email={mfaEmail}
				/>
			) : (
				<>
					{!email ? (
						<>
							<Box className={classes.signInWithContainer}>
								<BriaButton
									buttonType="outlined"
									variant="outlined"
									startIcon={<Google className={classes.icon} />}
									onClick={async () => {
										signUpUsingProvider(new GoogleAuthProvider());
									}}
								>
									{t("signUpWithGoogle")}
								</BriaButton>
								<BriaButton
									buttonType="outlined"
									variant="outlined"
									startIcon={<Github className={classes.icon} />}
									onClick={async () => {
										signUpUsingProvider(new GithubAuthProvider());
									}}
								>
									{t("signUpWithGithub")}
								</BriaButton>
								<BriaButton
									buttonType="outlined"
									variant="outlined"
									startIcon={<Microsoft className={classes.icon} />}
									onClick={async () => {
										signUpUsingProvider(new OAuthProvider(MICROSOFT_PROVIDER_ID));
									}}
								>
									{t("signUpWithMicrosoft")}
								</BriaButton>
							</Box>
							<Typography className={classes.or}>{t("or")}</Typography>
							<Formik
								initialValues={initialValues}
								validateOnMount={false}
								validateOnChange={false}
								validateOnBlur={false}
								validationSchema={Yup.object().shape({
									email: Yup.string().email(t("invalidEmail")).required(t("emailIsRequired")),
								})}
								onSubmit={(values) => {
									handleContinue(values);
								}}
							>
								{({ values, errors, handleChange, handleSubmit }) => (
									<Form className={classes.form} noValidate>
										<Box className={classes.formElements}>
											<Box className={classes.inputWrapper}>
												<BriaInput
													id="email"
													label={t("Email")}
													onChange={handleChange}
													value={values.email}
													error={!!errors.email}
													helperText={errors.email}
													className={classes.textField}
												/>

												{errorMessage && (
													<Box className={classes.errorWrapper}>
														<Typography className={classes.error} color="secondary">
															<ErrorIcon className={classes.errorIcon} />
															{t(`firebaseErrors.${errorMessage}`)}
														</Typography>
													</Box>
												)}
											</Box>

											<BriaButton
												buttonType="primary"
												onClick={() => handleSubmit(this)}
												variant="contained"
												color="secondary"
												fullWidth={true}
											>
												{t("signUp")}
											</BriaButton>
											<Box>
												<Agreement />
											</Box>
											<Typography className={classes.alreadyHaveAnAccount}>
												{t("alreadyHaveAnAccount")}
												<Link
													className={classes.signin}
													onClick={() => {
														navigate(`${RouterConstants.LOGIN.path}?${queryParams.toString()}`, {
															state: state,
														});
													}}
												>
													{t("logIn")}
												</Link>
											</Typography>
										</Box>
									</Form>
								)}
							</Formik>
						</>
					) : (
						<>
							<Typography className={classes.mainTitle}>{t("howDidYouHearAboutUs")}</Typography>
							<Formik
								initialValues={initialValues}
								validateOnMount={false}
								validateOnChange={false}
								validateOnBlur={false}
								validationSchema={Yup.object().shape({
									username: Yup.string().required(t("fullNameIsRequired")),
									password: Yup.string()
										.password()
										.required(t("passwordIsRequired"))
										.min(8, t("mustBe8OrMoreCharacters"))
										.minLowercase(1)
										.minUppercase(1)
										.minSymbols(1)
										.minNumbers(1),
								})}
								onSubmit={handleSubmit}
							>
								{({ values, errors, touched, handleChange, handleBlur }) => (
									<Form className={clsx(classes.form, classes.feedbackForm)} noValidate>
										<Box className={classes.formElements}>
											<BriaInput
												id="username"
												label={t("fullName")}
												onChange={handleChange}
												onBlur={handleBlur}
												value={values.username}
												error={!!touched.username && !!errors.username}
												helperText={touched.username ? errors.username : ""}
												height={continueFieldsHeight}
												className={classes.textField}
											/>

											<BriaInput
												id="password"
												label={t("password")}
												type="password"
												autoComplete="current-password"
												value={values.password}
												onChange={handleChange}
												onBlur={handleBlur}
												error={!!touched.password && !!errors.password}
												helperText={capitalizeFirstLetter(errors.password ? errors.password : "")}
												height={continueFieldsHeight}
												className={classes.textField}
											/>
											<StaticDropdown
												type={"userRoles"}
												className={classes.textField}
												value={userRole}
												height={continueFieldsHeight}
												width={DROPDOWN_WIDTH}
												menuMaxHeight={DROPDOWN_MAX_HEIGHT}
												placeholder={t("role")}
												loading={loading}
												disabled={loading}
												onChange={(e) => setUserRole(e.target.value)}
											/>
											<BriaButton
												buttonType="primary"
												type="submit"
												variant="contained"
												color="secondary"
												fullWidth={true}
											>
												{t("getStarted")}
												<Box className={classes.circleLoader}>
													{loading && <CircularProgress color="inherit" size={25} />}
												</Box>
											</BriaButton>

											{errorMessage && (
												<Typography className={classes.error} color="secondary">
													<ErrorIcon className={classes.errorIcon} />
													{t(`firebaseErrors.${errorMessage}`)}
												</Typography>
											)}
										</Box>
									</Form>
								)}
							</Formik>
							<Link className={classes.backToLogInPage} onClick={() => setEmail("")}>
								{t("backToRegistration")}
							</Link>
						</>
					)}
				</>
			)}
		</Box>
	);
};

export default Register;
